import type {
  ExploitRulesResponse,
  ExploitRuleHostsResponse,
  HostExploitRulesResponse,
  GlobalExploitRuleExclusion,
  HostExploitRuleExclusion,
  ExploitBlockRule,
  CreateExploitRuleRequest,
  UpdateExploitRuleRequest,
} from '../types/exploit-rules';
import { getAuthHeaders } from './auth';

const API_BASE = '/api/v1';

// Get all exploit block rules with global exclusion status
export async function fetchExploitRules(): Promise<ExploitRulesResponse> {
  const res = await fetch(`${API_BASE}/exploit-rules`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch exploit rules');
  return res.json();
}

// Get a single rule by ID
export async function fetchExploitRule(ruleId: string): Promise<ExploitBlockRule> {
  const res = await fetch(`${API_BASE}/exploit-rules/${ruleId}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch exploit rule');
  return res.json();
}

// Create a new custom rule
export async function createExploitRule(request: CreateExploitRuleRequest): Promise<ExploitBlockRule> {
  const res = await fetch(`${API_BASE}/exploit-rules`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || 'Failed to create exploit rule');
  }
  return res.json();
}

// Update a rule
export async function updateExploitRule(ruleId: string, request: UpdateExploitRuleRequest): Promise<ExploitBlockRule> {
  const res = await fetch(`${API_BASE}/exploit-rules/${ruleId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || 'Failed to update exploit rule');
  }
  return res.json();
}

// Delete a custom rule
export async function deleteExploitRule(ruleId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/exploit-rules/${ruleId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || 'Failed to delete exploit rule');
  }
}

// Toggle rule enabled status
export async function toggleExploitRule(ruleId: string): Promise<ExploitBlockRule> {
  const res = await fetch(`${API_BASE}/exploit-rules/${ruleId}/toggle`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || 'Failed to toggle exploit rule');
  }
  return res.json();
}

// Add global exclusion (disable rule for all hosts)
export async function addGlobalExploitExclusion(ruleId: string, reason?: string): Promise<GlobalExploitRuleExclusion> {
  const res = await fetch(`${API_BASE}/exploit-rules/${ruleId}/global-exclude`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || 'Failed to add global exclusion');
  }
  return res.json();
}

// Remove global exclusion (enable rule for all hosts)
export async function removeGlobalExploitExclusion(ruleId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/exploit-rules/${ruleId}/global-exclude`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || 'Failed to remove global exclusion');
  }
}

// Get hosts with exploit blocking enabled
export async function fetchExploitRuleHosts(): Promise<ExploitRuleHostsResponse> {
  const res = await fetch(`${API_BASE}/exploit-rules/hosts`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch exploit rule hosts');
  return res.json();
}

// Get rules with exclusion status for a specific host
export async function fetchHostExploitRules(hostId: string): Promise<HostExploitRulesResponse> {
  const res = await fetch(`${API_BASE}/exploit-rules/hosts/${hostId}/rules`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error('Failed to fetch host exploit rules');
  return res.json();
}

// Add host-specific exclusion
export async function addHostExploitExclusion(
  hostId: string,
  ruleId: string,
  reason?: string
): Promise<HostExploitRuleExclusion> {
  const res = await fetch(`${API_BASE}/exploit-rules/hosts/${hostId}/rules/${ruleId}/exclude`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || 'Failed to add host exclusion');
  }
  return res.json();
}

// Remove host-specific exclusion
export async function removeHostExploitExclusion(hostId: string, ruleId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/exploit-rules/hosts/${hostId}/rules/${ruleId}/exclude`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || 'Failed to remove host exclusion');
  }
}
